/**
 * 微信内打开 H5 时，自动引导跳转到焕己健康小程序
 * - 仅微信公众号（非企业微信、非小程序内嵌）内生效
 * - 兼容 / 与 /mp 等所有页面
 * - 复用后端公众号 jssdk 签名接口 getmpjssdkconfig（AppID wx8e27fb5064dd6f68）
 * - 落地页 pages/home/home，不携带参数
 * - installWechatJumpGuard 同步安装：先显示遮罩并拦截 OAuth 跳转，
 *   避免页面自身授权流程抢在跳转引导之前执行
 * - 注意：微信 iOS 内核中 window.location.replace 为只读属性，
 *   覆盖失败时必须静默降级，绝不能让异常中断 Vue 挂载（否则白屏）
 */
import { getMpJssdkConfig } from "@/service/mp/authorize";
import wx from "weixin-js-sdk";

const MINI_PROGRAM_USERNAME = "gh_8167128c20d4";
const MINI_PROGRAM_PATH = "pages/home/home";
const MINI_PROGRAM_SERVICE_PATH = "pages/service/service";

function resolveTargetPath(query) {
  const target = String((query && query.get && query.get("target")) || "").trim();
  if (target === "service") {
    return MINI_PROGRAM_SERVICE_PATH;
  }
  return MINI_PROGRAM_PATH;
}
const CONFIG_TIMEOUT = 5000;
const CONFIG_DELAY = 1500;

function isWechatBrowser() {
  const ua = (navigator.userAgent || "").toLowerCase();
  return ua.indexOf("micromessenger") !== -1;
}

function isWechatWork() {
  const ua = (navigator.userAgent || "").toLowerCase();
  return ua.indexOf("wxwork") !== -1;
}

function isMiniProgramWebview() {
  const ua = (navigator.userAgent || "").toLowerCase();
  return ua.indexOf("miniprogram") !== -1;
}

function isTestMode() {
  try {
    return localStorage.getItem("testMode") === "1";
  } catch (e) {
    return false;
  }
}

function shouldActivate() {
  return isWechatBrowser() && !isWechatWork() && !isMiniProgramWebview() && !isTestMode();
}

function removeMask() {
  const mask = document.getElementById("wx-jump-mask");
  if (mask && mask.parentNode) {
    mask.parentNode.removeChild(mask);
  }
}

function createMask() {
  if (!document.body) {
    return;
  }
  if (document.getElementById("wx-jump-mask")) {
    return;
  }
  const mask = document.createElement("div");
  mask.id = "wx-jump-mask";
  mask.style.cssText = "position:fixed;left:0;top:0;width:100%;height:100%;z-index:999999;background:rgba(0,0,0,0.65);display:flex;align-items:center;justify-content:center;";
  const panel = document.createElement("div");
  panel.style.cssText = "width:82%;max-width:320px;background:#fff;border-radius:12px;padding:28px 20px;text-align:center;box-sizing:border-box;";
  const title = document.createElement("div");
  title.textContent = "正在跳转“焕己健康”小程序";
  title.style.cssText = "font-size:18px;font-weight:600;color:#222;";
  panel.appendChild(title);
  mask.appendChild(panel);
  document.body.appendChild(mask);
}

/**
 * 是否应在挂载 Vue 前直接接管整页（所有页面在微信内一律展示跳转引导）
 * - 仅微信公众号（非企业微信、非小程序内嵌、非测试模式）
 */
export function shouldShowFullJump() {
  return shouldActivate();
}

/**
 * 全屏跳转引导页：同步接管 #app，不依赖 Vue 挂载
 * - 立即显示静态“正在跳转”引导
 * - 异步请求公众号 jssdk 签名，成功后创建 wx-open-launch-weapp 按钮
 * - 签名失败/超时时展示静态引导（提示在微信中搜索小程序）
 * - 任何异常都吞掉，绝不允许白屏
 */
export function showFullJumpPage() {
  try {
    if (document.getElementById("wx-full-jump")) {
      return;
    }
    const app = document.getElementById("app");
    if (app) {
      app.innerHTML = "";
    }

    const wrap = document.createElement("div");
    wrap.id = "wx-full-jump";
    wrap.style.cssText = "position:fixed;left:0;top:0;width:100%;height:100%;z-index:999999;background:#f7f8fa;display:flex;align-items:center;justify-content:center;";
    const panel = document.createElement("div");
    panel.style.cssText = "width:82%;max-width:320px;text-align:center;";
    const brand = document.createElement("div");
    brand.textContent = "焕己健康";
    brand.style.cssText = "font-size:26px;font-weight:700;color:#07c160;margin-bottom:16px;";
    const title = document.createElement("div");
    title.textContent = "正在跳转小程序…";
    title.style.cssText = "font-size:16px;color:#333;margin-bottom:8px;";
    const desc = document.createElement("div");
    desc.textContent = "即将打开“焕己健康”小程序";
    desc.style.cssText = "font-size:13px;color:#999;";
    panel.appendChild(brand);
    panel.appendChild(title);
    panel.appendChild(desc);
    wrap.appendChild(panel);
    document.body.appendChild(wrap);

    let targetPath = MINI_PROGRAM_PATH;
    try {
      const q = new URLSearchParams(window.location.search);
      targetPath = resolveTargetPath(q);
    } catch (e) {
      targetPath = MINI_PROGRAM_PATH;
    }
    let failShown = false;
    const showFail = () => {
      if (failShown) {
        return;
      }
      failShown = true;
      if (wrap && wrap.parentNode) {
        wrap.parentNode.removeChild(wrap);
      }
      createGuidance();
    };

    getMpJssdkConfig({ url: window.location.href.split("#")[0] })
      .then((res) => {
        const data = (res && res.data) || {};
        if (!data.appId || !data.signature) {
          showFail();
          return;
        }
        try {
          wx.config({
            debug: false,
            appId: data.appId,
            timestamp: data.timestamp,
            nonceStr: data.nonceStr,
            signature: data.signature,
            jsApiList: [],
            openTagList: ["wx-open-launch-weapp"]
          });
        } catch (e) {
          showFail();
          return;
        }
        wx.ready(() => {
          if (failShown) {
            return;
          }
          if (wrap && wrap.parentNode) {
            wrap.parentNode.removeChild(wrap);
          }
          const launchTag = createLaunchPage(targetPath);
          // 尽力自动触发；微信要求真实点击手势，未生效时用户点击按钮即可拉起
          setTimeout(() => {
            if (launchTag && launchTag.click) {
              launchTag.click();
            }
          }, 300);
        });
        wx.error(() => {
          showFail();
        });
        // 兜底：8 秒内未完成签名/拉起，转为静态引导
        setTimeout(showFail, 8000);
      })
      .catch(() => {
        showFail();
      });
  } catch (e) {
    // 兜底：即使接管失败也不白屏，退回正常 Vue 渲染
  }
}

/**
 * 同步安装守卫（需在 Vue 挂载前调用）：
 * - 标记跳转流程已启动，防止重复安装
 * - 拦截 OAuth 跳转，避免授权页抢在跳转引导前执行
 * - 立即显示全屏遮罩
 * - 任何异常都必须吞掉，保证不阻塞 Vue 挂载
 */
export function installWechatJumpGuard() {
  if (window.__wxJumpGuardInstalled) {
    return true;
  }
  if (!shouldActivate()) {
    return false;
  }
  window.__wxJumpGuardInstalled = true;

  try {
    const origReplace = window.location.replace && window.location.replace.bind(window.location);
    if (origReplace) {
      window.location.replace = (url) => {
        if (typeof url === "string" && url.indexOf("open.weixin.qq.com/connect/oauth2") !== -1) {
          // 微信内一律先引导跳小程序，不再发起 OAuth 授权
          return;
        }
        return origReplace(url);
      };
    }
  } catch (e) {
    // 微信 iOS 内核 location.replace 只读，覆盖失败时降级：
    // 不拦截 OAuth，但 OAuth 回跳后守卫仍会展示跳转引导层
  }

  createMask();
  return true;
}

function createLaunchPage(targetPath) {
  const tag = document.createElement("wx-open-launch-weapp");
  tag.setAttribute("username", MINI_PROGRAM_USERNAME);
  tag.setAttribute("path", targetPath || MINI_PROGRAM_PATH);
  tag.id = "wx-open-launch-weapp";
  tag.style.cssText = "position:fixed;left:0;top:0;width:100%;height:100%;z-index:999998;";

  const tpl = document.createElement("script");
  tpl.type = "text/wxtag-template";
  tpl.textContent = `
    <style>
      .wx-jump-mask { position: fixed; left: 0; top: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.65); display: flex; align-items: center; justify-content: center; }
      .wx-jump-panel { width: 82%; max-width: 320px; background: #fff; border-radius: 12px; padding: 28px 20px 20px; text-align: center; box-sizing: border-box; }
      .wx-jump-title { font-size: 20px; font-weight: 700; color: #07c160; margin-bottom: 10px; }
      .wx-jump-desc { font-size: 14px; color: #666; margin-bottom: 20px; line-height: 1.6; }
      .wx-jump-btn { width: 100%; height: 44px; border: none; border-radius: 22px; background: #07c160; color: #fff; font-size: 16px; }
      .wx-jump-tip { font-size: 12px; color: #999; margin-top: 12px; }
    </style>
    <div class="wx-jump-mask">
      <div class="wx-jump-panel">
        <div class="wx-jump-title">焕己健康</div>
        <div class="wx-jump-desc">点击下方按钮，跳转“焕己健康”小程序</div>
        <button class="wx-jump-btn">打开小程序</button>
        <div class="wx-jump-tip">点击后请稍候，将自动跳转小程序</div>
      </div>
    </div>`;
  tag.appendChild(tpl);
  document.body.appendChild(tag);
  return tag;
}

function createGuidance() {
  removeMask();
  const overlay = document.createElement("div");
  overlay.id = "wx-jump-guidance";
  overlay.style.cssText = "position:fixed;left:0;top:0;width:100%;height:100%;z-index:999999;background:rgba(0,0,0,0.65);display:flex;align-items:center;justify-content:center;";

  const panel = document.createElement("div");
  panel.style.cssText = "width:82%;max-width:320px;background:#fff;border-radius:12px;padding:28px 20px 20px;text-align:center;box-sizing:border-box;";

  const title = document.createElement("div");
  title.textContent = "欢迎使用焕己健康";
  title.style.cssText = "font-size:18px;font-weight:600;color:#222;margin-bottom:10px;";

  const desc = document.createElement("div");
  desc.textContent = "请在微信中打开“焕己健康”小程序，体验完整服务";
  desc.style.cssText = "font-size:14px;color:#666;margin-bottom:20px;line-height:1.6;";

  const tip = document.createElement("div");
  tip.textContent = "可点击右上角“...”选择“打开小程序”，或在微信中搜索“焕己健康”";
  tip.style.cssText = "font-size:12px;color:#999;line-height:1.6;";

  panel.appendChild(title);
  panel.appendChild(desc);
  panel.appendChild(tip);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  return overlay;
}

export function initWechatJump() {
  // 微信内已在 Vue 挂载前由 showFullJumpPage 接管整页，这里不再重复引导
  if (shouldShowFullJump()) {
    return;
  }
  if (!installWechatJumpGuard()) {
    return;
  }

  let settled = false;
  let timeoutId = null;
  let configTimer = null;
  let targetPath = MINI_PROGRAM_PATH;
  try {
    const q = new URLSearchParams(window.location.search);
    targetPath = resolveTargetPath(q);
  } catch (e) {
    targetPath = MINI_PROGRAM_PATH;
  }
  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (configTimer) {
      clearTimeout(configTimer);
      configTimer = null;
    }
  };

  getMpJssdkConfig({ url: window.location.href.split("#")[0] })
    .then((res) => {
      const data = (res && res.data) || {};
      if (!data.appId || !data.signature) {
        settled = true;
        cleanup();
        createGuidance();
        return;
      }
      // 延迟配置：等路由组件内已有的企微签名先执行完毕，避免其异步覆盖公众号开放标签配置
      configTimer = setTimeout(() => {
        try {
          wx.config({
            debug: false,
            appId: data.appId,
            timestamp: data.timestamp,
            nonceStr: data.nonceStr,
            signature: data.signature,
            jsApiList: [],
            openTagList: ["wx-open-launch-weapp"]
          });
        } catch (e) {
          if (!settled) {
            settled = true;
            cleanup();
            createGuidance();
          }
          return;
        }
        wx.ready(() => {
          if (settled) {
            return;
          }
          settled = true;
          cleanup();
          removeMask();
          const launchTag = createLaunchPage(targetPath);
          // 尽力自动触发；微信要求真实点击手势，未生效时用户点击按钮即可拉起
          setTimeout(() => {
            if (launchTag && launchTag.click) {
              launchTag.click();
            }
          }, 300);
        });
        wx.error(() => {
          if (settled) {
            return;
          }
          settled = true;
          cleanup();
          createGuidance();
        });
      }, CONFIG_DELAY);
      timeoutId = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        createGuidance();
      }, CONFIG_DELAY + CONFIG_TIMEOUT);
    })
    .catch(() => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      createGuidance();
    });
}
